/**
 * NotificationDot — the inverted count pill used on nav and notification chrome.
 *
 * Deliberately NOT a `Badge`. Badge is a tinted fill with a same-family light
 * label; this is the inverse — a solid bright fill with near-black text — so it
 * stays legible as a small overlay on an icon. Folding it into Badge would mean
 * giving Badge an inverted appearance that nothing else wants.
 *
 * This is an extraction, not a restyle: it renders what the sidebar, bells and
 * tab bar already rendered by hand, minus the drift between them.
 *
 * Positioning is the caller's job — pass it through `className`.
 */

import type {
  NotificationDotProps,
  NotificationDotSize,
  NotificationDotTone,
} from './NotificationDot.types';

const TONE_CLASSES: Record<NotificationDotTone, string> = {
  primary: 'bg-primary-300 text-primary-950',
  error: 'bg-error-400 text-contrast-950',
};

const SIZE_CLASSES: Record<NotificationDotSize, string> = {
  sm: 'h-4 min-w-4 px-1 text-label-xs',
  md: 'h-5 min-w-5 px-1.5 text-label-xs',
};

export function NotificationDot({
  count,
  max = 99,
  tone = 'primary',
  size = 'sm',
  showZero = false,
  className = '',
  'aria-label': ariaLabel,
}: NotificationDotProps): React.JSX.Element | null {
  const isNumeric = typeof count === 'number';

  if (isNumeric && count <= 0 && !showZero) return null;

  const display = isNumeric && count > max ? `${max}+` : `${count}`;

  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-bold leading-none ${TONE_CLASSES[tone]} ${SIZE_CLASSES[size]} ${className}`
        .replace(/\s+/g, ' ')
        .trim()}
      aria-label={ariaLabel ?? `${display} unread`}
      data-testid="notification-dot"
    >
      {display}
    </span>
  );
}

export default NotificationDot;
